'use client';

import { useCallback, useMemo } from 'react';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  PowerPlugAvailability,
  PowerPlugAvailabilityMap,
  PowerPlugId,
  getAvailablePowerPlugIds,
  getPowerPlugAvailabilityByRegion,
} from 'utils/powerPlugAvailability';

type UsePowerPlugAvailabilityResult = {
  byPlugId: PowerPlugAvailabilityMap;
  availablePlugIds: PowerPlugId[];
  getByPlugId: (id: PowerPlugId) => PowerPlugAvailability;
  isAvailable?: boolean;
};

export const usePowerPlugAvailability = (
  plugId?: PowerPlugId,
): UsePowerPlugAvailabilityResult => {
  const { region } = useRegionLocale();

  const byPlugId = useMemo(
    () => getPowerPlugAvailabilityByRegion(region),
    [region],
  );

  const availablePlugIds = useMemo(
    () => getAvailablePowerPlugIds(byPlugId),
    [byPlugId],
  );

  const getByPlugId = useCallback(
    (id: PowerPlugId) => byPlugId[id],
    [byPlugId],
  );

  const selectedPlugAvailability = useMemo(
    () => (plugId ? getByPlugId(plugId).isAvailable : undefined),
    [getByPlugId, plugId],
  );

  return {
    byPlugId,
    availablePlugIds,
    getByPlugId,
    isAvailable: selectedPlugAvailability,
  };
};

export default usePowerPlugAvailability;
